import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import GlobalContext from '../../../../../../context/global-context'
import { BusinessAPI } from '../../../../../../api/business.api'
import ResumenVinculosTable from './ResumenVinculosTable'
import { useResumenF29 } from './useResumenF29'
import ModalViewAdministracion from '../../../../ui/tables/baseTable/ModalViewAdministracion'
import { Select } from '../../../../ui'
import {
  currentYear,
  getYearsOptionsFromFirstLast
} from '../../../../../utils/constant'
  const currencyFormatter = new Intl.NumberFormat('es-CL')

  const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0)

  const safeNumber = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
  }

  const formatSummaryValue = (value) => {
    const number = safeNumber(value)
    const formatted = `$${formatCurrency(Math.abs(number))}`
    return number < 0 ? `(${formatted})` : formatted
  }

  const extractYearFromDateValue = (dateValue) => {
    if (!dateValue) return null

    if (typeof dateValue === 'string') {
      const yearMatch = dateValue.match(/^(\d{4})/)
      if (yearMatch) {
        return Number(yearMatch[1])
      }
    }

    const parsedDate = new Date(dateValue)
    if (Number.isNaN(parsedDate.getTime())) return null

    return parsedDate.getUTCFullYear()
  }

const SummaryList = ({ summaryItems, ppmInputValue, onPpmChange }) => {
  if (!summaryItems.length) {
    return <p className='text-xs text-gray-400'>Aún no hay datos para este período.</p>
  }

  const styles = {
    th: 'py-2 text-left px-2 xs:px-2 md:px-3',
    td: 'py-3 align-top text-left px-2',
  }

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-xl border border-gray-100 bg-primary p-3">
      <table className="h-full w-full min-w-[260px] table-fixed border-collapse text-xs xs:text-xs">
        <tbody>
          {summaryItems.map((item, idx) => {
            const isPpmRow = item.key === 'ppm'
            const isZeroRetention = item.key === 'retencion_boleta' && safeNumber(item.value) === 0
            const isTotal = item.key === 'total_pagar'
            const valueText = isZeroRetention ? '0' : formatSummaryValue(item.value)
            const rowBg = idx % 2 === 0 ? 'bg-primary' : 'bg-secondary/20'

            return (
              <tr key={item.key}>
                <td
                  className={`
                    ${styles.td}
                    w-[68%]
                    ${rowBg}
                    break-words whitespace-normal
                    ${isTotal ? 'bg-primary text-yellow-300 font-semibold text-xs' : 'text-white'}
                  `}
                >
                  {isPpmRow ? (
                    <div className='flex flex-wrap items-center gap-2'>
                      <span>PPM</span>
                      <div className='flex items-center gap-1 text-[11px]'>
                        <input
                          type='number'
                          min='0'
                          step='0.01'
                          value={ppmInputValue}
                          onChange={onPpmChange}
                          className='w-16 sm:w-20 rounded border border-gray-300 px-1 py-1 text-[10px] sm:text-[11px] text-black'
                          placeholder='0.00'
                        />
                        <span className='text-[10px] text-gray-300'>%</span>
                      </div>
                    </div>
                  ) : (
                    item.label
                  )}
                </td>

                <td
                  className={`
                    ${styles.td}
                    w-[32%]
                    ${rowBg}
                    text-right
                    whitespace-nowrap
                    ${isTotal ? 'bg-primary text-yellow-300 font-bold text-xs' : 'text-white'}
                  `}
                >
                  {valueText}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const ResumenImpuestos = () => {
  const { currentBusiness, firstLast, ui } = useContext(GlobalContext)
  const businessAPI = useMemo(() => new BusinessAPI(), [])
  const [vinculosRows, setVinculosRows] = useState([])
  const [isLoadingVinculos, setIsLoadingVinculos] = useState(false)
  const [switchGetData, setSwitchGetData] = useState(false)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const yearOptions = useMemo(
    () => getYearsOptionsFromFirstLast(firstLast.data, currentBusiness?.createdAt),
    [firstLast.data, currentBusiness?.createdAt]
  )

  useEffect(() => {
    if (!firstLast.isLoading && !yearOptions.some(option => option.value === Number(yearFilter))) {
      setYearFilter(currentYear)
    }
  }, [firstLast.isLoading, yearFilter, yearOptions])
  const today    = new Date()
  const yearNum  = today.getFullYear()

  const [comprobanteModal, setComprobanteModal] = useState({
    open: false,
    libroId: '',
    nombreFolio: '',
    folio: ''
  })
  const {
    summaryItems,
    //month,
    ppmInputValue,
    handlePpmInputChange,
  } = useResumenF29({
    businessId: currentBusiness?.id,
    businessAPI,
    ui,
  })

  const handleOpenComprobante = (row) => {
    const libroId =
      row?.idPrincipal ||
      row?.id ||
      row?.libroId ||
      row?.linkageId ||
      ''
    setComprobanteModal({
      open: true,
      libroId,
      nombreFolio: row.nombreFolio || 'Formulario 29',
      folio: row.numeroFolio || row.folio || ''
    })
  }

  const handleCloseComprobante = () => {
    setComprobanteModal({
      open: false,
      libroId: '',
      nombreFolio: '',
      folio: ''
    })
  }

    const fetchVinculos = useCallback(async () => {
      const selectedYear = Number(yearFilter) || yearNum

      setIsLoadingVinculos(true)

      try {
        const response = await businessAPI.getListSummaryMovements({
          businessId: currentBusiness.id,
          type: 'doc-29',
          month: 0,
          year: selectedYear,
          max: 30,
          page: 0,
          linkage: false
        })

        const rawRows =
          response?.data?.libros ||
          response?.data?.data ||
          response?.data ||
          []

        const rows = Array.isArray(rawRows)
          ? rawRows
          : []

        const filteredRows = rows.filter(
          row =>
            extractYearFromDateValue(row?.fecha) === selectedYear
        )

        setVinculosRows(filteredRows)

      } catch (e) {
        setVinculosRows([])
      } finally {
        setIsLoadingVinculos(false)
      }
    }, [
      businessAPI,
      currentBusiness?.id,
      yearFilter,
      yearNum
    ])

  useEffect(() => {
    setIsLoadingVinculos(true)
    fetchVinculos()
    setSwitchGetData(false)
  }, [fetchVinculos,switchGetData === true,yearFilter])

  const meses = [ 'Enero','Febrero','Marzo','Abril','Mayo','Junio', 'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
  const mesActual = meses[new Date().getMonth()]

  return (
    <>
      <div className="p-4 mt-6 bg-white rounded-3xl shadow-md flex flex-col font-sans">
        <div className="flex flex-col gap-6 items-stretch xl:flex-row">
        {/* PANEL IZQUIERDO */}
        <div className="flex w-full shrink-0 xl:w-[340px] xl:min-w-[320px]">
          <div className="flex w-full flex-col rounded-2xl border border-gray-200 bg-primary p-4 shadow-sm sm:p-5">
            <p className="text-[13px] sm:text-[15px] font-semibold uppercase tracking-[0.15em] sm:tracking-[0.2em] text-yellow-300 text-center">
              Proyección Formulario 29 {mesActual}
            </p>
            {/* IMPORTANTE */}
            <div className="mt-3 flex-1 min-w-0 overflow-visible">
              <SummaryList
                summaryItems={summaryItems}
                ppmInputValue={ppmInputValue}
                onPpmChange={handlePpmInputChange}
              />
            </div>
          </div>
        </div>

        <div className="flex w-full min-w-0 flex-1">
          <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white/95 p-3 shadow-sm">
            <div className="w-40 shrink-0">
              <Select options={yearOptions} value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} />
            </div>

            {/* IMPORTANTE */}
            <div className="flex-1 min-w-0 overflow-x-auto">
              <ResumenVinculosTable
                rows={vinculosRows}
                isLoading={isLoadingVinculos}
                onOpenComprobante={handleOpenComprobante}
                setSwitchGetData={setSwitchGetData}
              />
            </div>
          </div>
        </div>
        </div>

      {comprobanteModal.open && (
        <ModalViewAdministracion
          openModal={comprobanteModal.open}
          setOpenModal={handleCloseComprobante}
          comprobanteId={comprobanteModal.libroId}
          validation={1}
          folio={comprobanteModal.nombreFolio}
          numeroFolio={comprobanteModal.folio}
        />
      )}
      </div>
    </>
  )
}

export default ResumenImpuestos
